import { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonItem,
  IonInput,
  IonTextarea,
  IonLabel,
  IonNote,
  IonIcon,
  IonBackButton,
  IonButtons,
  IonToast,
  IonToggle,
  IonCard,
  IonCardContent,
  IonSpinner,
} from '@ionic/react';
import { add, close, create } from 'ionicons/icons';
import { z } from 'zod';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import {
  useMyCandidateProfile,
  useCandidateExperience,
  useCreateWorkExperience,
  useUpdateWorkExperience,
  useDeleteWorkExperience,
} from '@/lib/api/hooks';
import type { WorkExperience } from '@/types';

const schema = z.object({
  company: z.string().min(1, 'Company name is required'),
  title: z.string().min(1, 'Job title is required'),
  description: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  location: z.string().optional(),
});

interface ExperienceEntry {
  company: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  current: boolean;
  location: string;
}

export function ExperienceManager() {
  const { data: candidateEnvelope } = useMyCandidateProfile();
  const candidateId = candidateEnvelope?.data?._id ?? '';
  const { data: experiences, isLoading } = useCandidateExperience(candidateId);

  const createExp = useCreateWorkExperience();
  const updateExp = useUpdateWorkExperience();
  const deleteExp = useDeleteWorkExperience();

  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<ExperienceEntry>({
    company: '', title: '', description: '', startDate: '', endDate: '', current: false, location: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  const set = (patch: Partial<ExperienceEntry>) => setForm((f) => ({ ...f, ...patch }));

  const startAdd = () => {
    setAdding(true);
    setEditing(null);
    setForm({ company: '', title: '', description: '', startDate: '', endDate: '', current: false, location: '' });
    setErrors({});
  };

  const startEdit = (exp: WorkExperience) => {
    setEditing(exp._id);
    setAdding(false);
    setForm({
      company: exp.companyName,
      title: exp.jobTitle,
      description: exp.description ?? '',
      startDate: exp.startDate,
      endDate: exp.endDate ?? '',
      current: exp.isCurrent,
      location: exp.workLocation?.label ?? '',
    });
    setErrors({});
  };

  const handleSave = () => {
    const r = schema.safeParse({
      company: form.company,
      title: form.title,
      description: form.description || undefined,
      startDate: form.startDate,
      location: form.location || undefined,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
      setErrors(e);
      return;
    }
    setErrors({});

    const payload = {
      candidateProfile: { type: 'candidateprofile' as const, id: candidateId },
      companyName: form.company,
      jobTitle: form.title,
      description: form.description || undefined,
      startDate: form.startDate,
      endDate: form.current ? undefined : form.endDate || undefined,
      isCurrent: form.current,
      workLocation: form.location ? { label: form.location } : undefined,
      status: 'Active' as const,
    };

    if (editing) {
      updateExp.mutate(
        { id: editing, data: payload, candidateId },
        {
          onSuccess: () => {
            setToast({ msg: 'Experience updated', color: 'success' });
            setEditing(null);
            setAdding(false);
          },
          onError: (err: Error) => {
            setToast({ msg: err.message || 'Failed to update', color: 'danger' });
          },
        },
      );
    } else {
      createExp.mutate(
        { candidateId, data: payload },
        {
          onSuccess: () => {
            setToast({ msg: 'Experience added', color: 'success' });
            setAdding(false);
          },
          onError: (err: Error) => {
            setToast({ msg: err.message || 'Failed to add', color: 'danger' });
          },
        },
      );
    }
  };

  const remove = (id: string) => {
    deleteExp.mutate(
      { id, candidateId },
      {
        onSuccess: () => {
          setToast({ msg: 'Experience removed', color: 'medium' });
        },
        onError: (err: Error) => {
          setToast({ msg: err.message || 'Failed to remove', color: 'danger' });
        },
      },
    );
  };

  const isFormOpen = adding || editing !== null;

  if (isLoading) {
    return (
      <IonPage>
        <NoindexSeo />
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/profile" /></IonButtons>
            <IonTitle>Work Experience</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding" style={{ textAlign: 'center', paddingTop: '3rem' }}>
          <IonSpinner name="crescent" />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/profile" />
          </IonButtons>
          <IonTitle>Work Experience</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {(experiences ?? []).map((exp: WorkExperience) => (
          <IonCard key={exp._id}>
            <IonCardContent>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0 }}>{exp.jobTitle}</h3>
                  <IonLabel color="medium">{exp.companyName}</IonLabel>
                  <p style={{ margin: '4px 0', fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                    {exp.startDate} – {exp.isCurrent ? 'Present' : exp.endDate}
                  </p>
                </div>
                <div>
                  <IonButton fill="clear" onClick={() => startEdit(exp)}>
                    <IonIcon icon={create} />
                  </IonButton>
                  <IonButton fill="clear" color="danger" onClick={() => remove(exp._id)}>
                    <IonIcon icon={close} />
                  </IonButton>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        ))}

        {!isFormOpen && (
          <IonButton expand="block" fill="outline" onClick={startAdd}>
            <IonIcon icon={add} slot="start" /> Add experience
          </IonButton>
        )}

        {isFormOpen && (
          <IonCard color="light">
            <IonCardContent>
              <IonLabel style={{ fontWeight: 600 }}>{editing ? 'Edit' : 'Add'} experience</IonLabel>
              <IonItem>
                <IonInput label="Company" value={form.company} onIonInput={(e) => set({ company: e.detail.value! })} />
              </IonItem>
              {errors.company && <IonNote color="danger">{errors.company}</IonNote>}
              <IonItem>
                <IonInput label="Job title" value={form.title} onIonInput={(e) => set({ title: e.detail.value! })} />
              </IonItem>
              {errors.title && <IonNote color="danger">{errors.title}</IonNote>}
              <IonItem>
                <IonTextarea label="Description" value={form.description} onIonInput={(e) => set({ description: e.detail.value! })} rows={2} />
              </IonItem>
              <IonItem>
                <IonInput label="Start date" type="date" value={form.startDate} onIonInput={(e) => set({ startDate: e.detail.value! })} />
              </IonItem>
              {errors.startDate && <IonNote color="danger">{errors.startDate}</IonNote>}
              {!form.current && (
                <IonItem>
                  <IonInput label="End date" type="date" value={form.endDate} onIonInput={(e) => set({ endDate: e.detail.value! })} />
                </IonItem>
              )}
              <IonItem>
                <IonLabel>Current position</IonLabel>
                <IonToggle slot="end" checked={form.current} onIonChange={(e) => set({ current: e.detail.checked })} />
              </IonItem>
              <IonItem>
                <IonInput label="Location" value={form.location} onIonInput={(e) => set({ location: e.detail.value! })} />
              </IonItem>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <IonButton fill="clear" onClick={() => { setEditing(null); setAdding(false); }}>Cancel</IonButton>
                <IonButton onClick={handleSave} disabled={createExp.isPending || updateExp.isPending}>
                  {createExp.isPending || updateExp.isPending ? 'Saving…' : editing ? 'Update' : 'Add'}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        )}
        <AppFooter />
      </IonContent>
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}
